/// <amd-module name="Repos/Discussion/Components/ReplyComment" />
import * as React from "react";
import { DiscussionStatus } from "Repos/Discussion/Common/DiscussionContracts";
import * as Resources from "Repos/Discussion/Resources";
import { ScreenContext, ScreenSize } from "VSS/Core/Util/Screen";
import { Button } from "VSSUI/Button";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { SplitButton } from "VSSUI/SplitButton";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import { isActiveCommentStatus } from "../Utils/DiscussionStatus";
import { EditComment } from "./EditComment";
import { IReplyCommentProps } from "./ReplyComment.Props";

export const ReplyComment = (props: IReplyCommentProps): JSX.Element => {
    const {
        author,
        className,
        comment,
        commentEditorClassName,
        markdownEditorProps,
        onCancel,
        onChangeStatus,
        onReplyComment,
        personaProvider,
        status
    } = props;

    const submitChanges = (status?: DiscussionStatus): void => {
        const content = comment.getContent().value;
        content && onReplyComment && onReplyComment(comment.getId(), content, status);
    };

    const [hasBeenCancelled, setCancelled] = React.useState(false);
    const cancelChanges = (): void => {
        comment.revertComment();

        if (onCancel) {
            onCancel();
        }

        setCancelled(true);
    };

    return (
        <div className={css(className, "flex-column")}>
            <Observer editing={comment.isEditing()} saving={comment.isSaving()}>
                {(commentProps: { editing: boolean; saving: boolean }) => {
                    if (commentProps.editing) {
                        const buttons = (
                            <>
                                <Button disabled={commentProps.saving} onClick={cancelChanges}>
                                    {Resources.CancelButtonText}
                                </Button>
                                <ScreenContext.Consumer>
                                    {screen => (
                                        <Observer hasContent={comment.hasContent()} screenSize={screen.size} status={status}>
                                            {(buttonProps: { hasContent: boolean; screenSize: ScreenSize; status: DiscussionStatus }) => {
                                                if (buttonProps.screenSize <= ScreenSize.xsmall) {
                                                    return (
                                                        <SplitButton
                                                            buttonProps={{
                                                                onClick: () => submitChanges(),
                                                                text: Resources.ReplyButtonText
                                                            }}
                                                            disabled={!buttonProps.hasContent || commentProps.saving}
                                                            primary={true}
                                                            menuButtonProps={{
                                                                contextualMenuProps: {
                                                                    menuProps: {
                                                                        id: "reply-split-button-menu",
                                                                        items: [
                                                                            {
                                                                                id: "reply-menu-item",
                                                                                onActivate: () => submitChanges(),
                                                                                text: Resources.ReplyButtonText
                                                                            },
                                                                            {
                                                                                id: "reply-and-resolve-menu-item",
                                                                                onActivate: () =>
                                                                                    submitChanges(
                                                                                        isActiveCommentStatus(buttonProps.status)
                                                                                            ? DiscussionStatus.Fixed
                                                                                            : DiscussionStatus.Active
                                                                                    ),

                                                                                text: isActiveCommentStatus(buttonProps.status)
                                                                                    ? Resources.ReplyAndResolveButtonText
                                                                                    : Resources.ReplyAndReactivateButtonText
                                                                            }
                                                                        ]
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    );
                                                }
                                                return (
                                                    <>
                                                        <Button
                                                            disabled={!buttonProps.hasContent || commentProps.saving}
                                                            onClick={() =>
                                                                submitChanges(
                                                                    isActiveCommentStatus(buttonProps.status)
                                                                        ? DiscussionStatus.Fixed
                                                                        : DiscussionStatus.Active
                                                                )
                                                            }
                                                        >
                                                            {isActiveCommentStatus(buttonProps.status)
                                                                ? Resources.ReplyAndResolveButtonText
                                                                : Resources.ReplyAndReactivateButtonText}
                                                        </Button>
                                                        <Button
                                                            disabled={!buttonProps.hasContent || commentProps.saving}
                                                            onClick={() => submitChanges()}
                                                            primary={true}
                                                        >
                                                            {Resources.ReplyButtonText}
                                                        </Button>
                                                    </>
                                                );
                                            }}
                                        </Observer>
                                    )}
                                </ScreenContext.Consumer>
                            </>
                        );

                        return (
                            <EditComment
                                author={author}
                                buttons={buttons}
                                className={commentEditorClassName}
                                comment={comment.getContent()}
                                dirty={comment.isDirty()}
                                error={comment.getError()}
                                markdownEditorProps={markdownEditorProps}
                                onCancel={cancelChanges}
                                onChange={newComment => comment.setContent(newComment)}
                                onError={error => comment.setError(error)}
                                onSubmit={submitChanges}
                                personaClassName="margin-top-4"
                                personaProvider={personaProvider}
                                saving={comment.isSaving()}
                            />
                        );
                    } else {
                        return (
                            <div className="flex-row flex-grow rhythm-horizontal-8">
                                <Persona
                                    className="margin-right-4 margin-top-4"
                                    identity={author}
                                    personaProvider={personaProvider}
                                    size={PersonaSize.size24}
                                />
                                <div className="flex-row flex-grow flex-center rhythm-horizontal-8 repos-comment-editor-max-width">
                                    <TextField
                                        autoFocus={hasBeenCancelled}
                                        containerClassName="flex-grow padding-vertical-4"
                                        onClick={() => comment.startEditing()}
                                        onChange={(e, value) => {
                                            comment.startEditing();
                                            comment.setContent(value);
                                        }}
                                        placeholder={Resources.WriteAReplyText}
                                    />
                                    <ScreenSizeConditional condition={size => size > ScreenSize.xsmall}>
                                        <Observer status={status}>
                                            {(buttonProps: { status: DiscussionStatus }) => {
                                                return (
                                                    <Button
                                                        onClick={() =>
                                                            onChangeStatus &&
                                                            onChangeStatus(
                                                                isActiveCommentStatus(buttonProps.status)
                                                                    ? DiscussionStatus.Fixed
                                                                    : DiscussionStatus.Active
                                                            )
                                                        }
                                                    >
                                                        {isActiveCommentStatus(buttonProps.status)
                                                            ? Resources.ResolveButtonText
                                                            : Resources.ReactivateButtonText}
                                                    </Button>
                                                );
                                            }}
                                        </Observer>
                                    </ScreenSizeConditional>
                                </div>
                            </div>
                        );
                    }
                }}
            </Observer>
        </div>
    );
};
